import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  delay,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  startWith,
  Subscription,
  take,
  tap,
  switchMap,
} from 'rxjs';

import { CustomIcon, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  ElementActions,
  ElementTypes,
  FragmentService,
  GaContext,
  MediaQueryService,
  ScrollService,
  ToasterService,
} from '@fcom/common';
import {
  categoryPartStatus,
  categoryStatusMap,
  CommonBookingFeatureState,
  orderData,
  OrderPartStatus,
  serviceAvailability,
  serviceCatalogCampaigns,
  serviceCategories,
  ServicesActions,
  servicesStatus,
  upsell,
} from '@fcom/common-booking';
import { ProductCardNotification, SmpProduct } from '@fcom/common-booking/modules/ancillaries/components';
import { ButtonState } from '@fcom/common-booking/modules/ancillaries/interfaces';
import {
  categoryHasMaxPurchasesDoneForFragments,
  categoryHasNothingToSell,
  categoryHasQuotaLeft,
  combineServices,
  filterCategories,
  getCountForCategoryStream,
  getLowestPriceForCategory,
  getMaxNumberItemsForCategory,
  getPriceForCategoryStream,
  getProductTags,
  getServicesToShow,
  isPetcNotEligibleWithIncompatibleSeat,
  isSeatPurchaseClosedAfterCheckIn,
} from '@fcom/common-booking/modules/ancillaries/utils';
import {
  bookingHasDisruption,
  passengersMissingCategoryService,
  shouldShowKidsInBusinessNotification,
  shouldWeSellForCategory,
} from '@fcom/common-booking/utils/common-booking.utils';
import { GtmService } from '@fcom/common/gtm';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { FeedbackService } from '@fcom/common/services/feedback/feedback.service';
import {
  Category,
  FinnairOrder,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairServiceCatalogCampaign,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';
import {
  FinnairServiceItemWithUpsell,
  ModifiedServiceProgress,
  SelectedServiceData,
  ServicesStatus,
  UpsellCategoriesForBound,
} from '@fcom/dapi/interfaces';
import { finShare, snapshot } from '@fcom/rx';
import { ButtonMode, ButtonTheme, ButtonType, IconPosition } from '@fcom/ui-components';
import { LoaderTheme } from '@fcom/ui-components/components/loader/enums';
import { ModalButtons } from '@fcom/ui-components/components/modal/interfaces';
import { NotificationLayout, NotificationTheme } from '@fcom/ui-components/components/notifications';
import { EMAIL_PATTERN, unique, unsubscribe } from '@fcom/core/utils';
import { ConfigService, RootPaths } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';
import { CmsArticle } from '@fcom/core-api/interfaces';
import { serviceCatalogCategoriesAreSame } from '@fcom/common/utils/booking-common.utils';
import { AllowedActionsMode } from '@fcom/common-booking/modules/ancillaries/enums';
import { CheckInService } from '@fcom/manage-booking-common';
import { getEligibleAllianzInsuranceId } from '@fcom/common-booking/utils/order.utils';

import { MmbError, TravelInsuranceEligibilityId } from '../../../enums';
import { ManageBookingService } from './../../../services/manage-booking.service';
import { removeAllProducts } from '../../../utils/ancillaries.util';
import { CheckInRedirectionType } from '../../check-in/interface/check-in.interface';

interface UnpaidItemsModal {
  isOpen: boolean;
  checkInRedirection?: CheckInRedirectionType;
}

@Component({
  selector: 'fin-mmb-view-route',
  templateUrl: './view-route.component.html',
  styleUrls: ['./view-route.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingViewRouteComponent implements OnInit, OnDestroy {
  private store$ = inject<Store<CommonBookingFeatureState>>(Store);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private mmbService = inject(ManageBookingService);
  private gtmService = inject(GtmService);
  private toasterService = inject(ToasterService);
  private feedbackService = inject(FeedbackService);
  private scrollService = inject(ScrollService);
  private element = inject(ElementRef);
  private languageService = inject(LanguageService);
  private configService = inject(ConfigService);
  private fragmentService = inject(FragmentService);
  private checkInService = inject(CheckInService);
  private mediaqueryService = inject(MediaQueryService);

  public readonly ModalButtons = ModalButtons;
  public readonly LoaderTheme = LoaderTheme;
  public readonly NotificationTheme = NotificationTheme;
  public readonly NotificationLayout = NotificationLayout;
  public readonly ButtonType = ButtonType;
  public readonly AllowedActionsMode = AllowedActionsMode;
  public readonly CustomIcon = CustomIcon;
  public readonly SvgLibraryIcon = SvgLibraryIcon;
  public readonly ButtonMode = ButtonMode;
  public readonly ButtonTheme = ButtonTheme;
  public readonly IconPosition = IconPosition;

  @ViewChild('travelExtrasSection', { static: false, read: ElementRef }) travelExtrasSection: ElementRef;

  @Output() focusEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() blurEvent: EventEmitter<void> = new EventEmitter<void>();

  booking$: Observable<FinnairOrder>;
  pax$: Observable<FinnairPassengerItem>;
  products$: Observable<SmpProduct[]>;
  changeFlightModalOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showGroupBookingEmailModal$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  groupEmailForm: FormGroup<Record<'email', FormControl<string>>>;
  loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  groupModalError$: Observable<MmbError>;
  isTravelExtraModal = false;
  includedServices$: Observable<FinnairServiceItem[]>;
  combinedServices$: Observable<FinnairServiceItemWithUpsell[]>;
  unconfirmedServices$: Observable<FinnairServiceItem[]>;
  bookingIsDisrupted$: Observable<boolean>;
  upsellInModal$: Observable<UpsellCategoriesForBound>;
  productsReady$: Observable<boolean>;
  showChildSafetyNotice$: Observable<boolean>;
  openAncillarySubview$: ReplaySubject<SelectedServiceData> = new ReplaySubject<SelectedServiceData>(1);
  kidsInBusinessModalOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  enableShowingSAF: boolean;
  enableKidsInBusinessNotification: boolean;
  enableAllianzThirdPartyAncillary: boolean;
  content$: Observable<CmsArticle[]>;
  unpaidItemsModalContent$: Observable<CmsArticle>;
  categoryStatus$: Observable<Partial<Record<Category, OrderPartStatus>>>;
  serviceModifyProgress$: Observable<ModifiedServiceProgress>;
  isMobile$: Observable<boolean>;
  hasUnpaidServices$: Observable<boolean>;
  isBoardingPassAvailable$: Observable<boolean>;
  unpaidServicesInCartModal$: BehaviorSubject<UnpaidItemsModal> = new BehaviorSubject<UnpaidItemsModal>({
    isOpen: false,
  });
  clearingServicesInProgress$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  visibleAllianzBannerId$: Observable<TravelInsuranceEligibilityId | null>;
  TravelInsuranceEligibilityId = TravelInsuranceEligibilityId;

  private subscriptions = new Subscription();

  private getStatusStream = (
    category: Category,
    numberOfUnpaidServices$: Observable<number>,
    order: FinnairOrder
  ): Observable<ButtonState> => {
    const unpaidButtonState$ = numberOfUnpaidServices$.pipe(
      map((n) => {
        if (
          category === Category.SEAT &&
          order.services.included
            .filter((service) => service.category === Category.SEAT)
            .map((includedSeatService) =>
              includedSeatService.bounds
                .flatMap(({ segments }) => segments)
                .flatMap(({ passengers }) => passengers)
                .flatMap(({ services }) => services)
                .some(({ modifiable }) => modifiable)
            )
            .some(Boolean)
        ) {
          return ButtonState.OK;
        }
        return n > 0 ? ButtonState.OK : ButtonState.PLUS;
      })
    );
    return combineLatest([this.store$.pipe(categoryPartStatus(category)), unpaidButtonState$]).pipe(
      map(([status, unpaidButtonState]) =>
        status === OrderPartStatus.UPDATING ? ButtonState.UPDATING : unpaidButtonState
      )
    );
  };

  ngOnInit(): void {
    this.isMobile$ = this.mediaqueryService.isBreakpoint$('mobile');
    this.enableShowingSAF = this.configService.cfg.enableShowingSAF;
    this.enableKidsInBusinessNotification = this.configService.cfg.enableKidsInBusinessNotification;
    this.enableAllianzThirdPartyAncillary = this.configService.cfg.enableAllianzThirdPartyAncillary;
    this.booking$ = this.store$.pipe(orderData());
    this.categoryStatus$ = this.store$.pipe(categoryStatusMap());
    this.serviceModifyProgress$ = this.categoryStatus$.pipe(
      map((categoryStatus) => {
        const categoryIsUpdating = Object.keys(categoryStatus).find((category) => {
          return categoryStatus[category as Category] === OrderPartStatus.UPDATING;
        });

        return {
          modifyInProgress: Boolean(categoryIsUpdating),
          services: categoryIsUpdating
            ? [
                {
                  category: categoryIsUpdating as Category,
                },
              ]
            : [],
        };
      })
    );
    this.showChildSafetyNotice$ = this.enableKidsInBusinessNotification
      ? this.booking$.pipe(
          map((booking) =>
            booking.bounds.some((bound) => shouldShowKidsInBusinessNotification([bound], booking.passengers))
          )
        )
      : of(false);

    this.pax$ = this.booking$.pipe(map((booking) => booking.passengers[0]));
    this.feedbackService.setSurvey(QualtricsSurveys.MMB_FEEDBACK);
    this.feedbackService.setFeedbackWidgetVisibility(true);

    this.bookingIsDisrupted$ = this.booking$.pipe(
      map((booking: FinnairOrder) => bookingHasDisruption(booking)),
      finShare()
    );

    this.visibleAllianzBannerId$ = getEligibleAllianzInsuranceId(this.booking$, this.configService.cfg);

    this.subscriptions.add(
      this.bookingIsDisrupted$
        .pipe(
          filter((isDisrupted) => !isDisrupted && !!this.router.parseUrl(this.router.url).fragment),
          distinctUntilChanged(),
          delay(1)
        )
        .subscribe(() => {
          const scrollElementContainer = this.element.nativeElement.querySelector(
            `.${this.router.parseUrl(this.router.url).fragment}`
          );
          if (scrollElementContainer) {
            this.scrollTo(scrollElementContainer, 100);
          }
        })
    );

    const fallbackCurrencyCode$ = this.booking$.pipe(
      map((order) => order?.prices?.included?.total?.total.totalAmount.currencyCode)
    );
    const includedServices$ = this.booking$.pipe(
      map((order) => order?.services?.included || []),
      finShare()
    );

    this.combinedServices$ = this.booking$.pipe(
      map((order) => combineServices([order?.services?.included || [], order?.services?.unpaid || []])),
      finShare()
    );

    const unpaidServices$ = this.store$.pipe(
      orderData(),
      map((order: FinnairOrder) => order?.services?.unpaid || [])
    );

    const availability$ = this.store$.pipe(serviceAvailability());

    const campaignsCategories$ = this.store$.pipe(
      serviceCatalogCampaigns(),
      startWith([] as FinnairServiceCatalogCampaign[]),
      map((campaigns) =>
        campaigns
          .flatMap((c) => c.categories)
          .flatMap((c) => c.id)
          .filter(unique)
      ),
      finShare()
    );

    this.subscriptions.add(
      this.store$
        .pipe(
          upsell(),
          filter(Boolean),
          tap(() => {
            this.store$.dispatch(ServicesActions.clearUpsell());
          })
        )
        .subscribe((data) => {
          this.openAncillarySubview$.next(data);
        })
    );

    this.products$ = combineLatest([
      combineLatest([
        this.store$.pipe(
          serviceCategories(),
          distinctUntilChanged((prev, next) => serviceCatalogCategoriesAreSame(prev, next)),
          map((categories) => filterCategories(categories, this.configService.cfg.enablePriorityServices)),
          startWith(undefined)
        ),
        this.store$.pipe(servicesStatus()),
      ]).pipe(
        map(([categories, servicesStatus]) => {
          if (!categories && servicesStatus === ServicesStatus.INITIAL) {
            return [];
          }
          return categories;
        }),
        filter(Boolean)
      ),
      this.booking$.pipe(filter((booking) => booking?.eligibilities?.serviceCatalog?.isAllowedToUse)),
      availability$,
      campaignsCategories$,
      this.combinedServices$,
    ]).pipe(
      map(([categories, booking, availability, campaignsCategories, combinedServices]) =>
        categories.map((category): SmpProduct => {
          const numberOfUnpaidServices$ = getCountForCategoryStream(category.category, unpaidServices$);
          const maxNumberItems$ = getMaxNumberItemsForCategory(category.category, categories, booking);
          const purchasesDoneForFragment$ = categoryHasMaxPurchasesDoneForFragments(
            booking?.passengers.filter((passenger) => passenger.passengerTypeCode !== FinnairPassengerCode.INF).length,
            category.category,
            includedServices$,
            categories,
            booking,
            availability
          );

          const notAvailable$ = categoryHasNothingToSell(category.category, availability$, booking);
          const noQuotaLeft = !categoryHasQuotaLeft(category.category, categories);
          const maxPurchasesDone$ = purchasesDoneForFragment$.pipe(
            map((purchases) => Object.values(purchases).length > 0 && Object.values(purchases).every(Boolean))
          );
          const notEligibleWithIncompatibleSeat = isPetcNotEligibleWithIncompatibleSeat(category.category, booking);
          const lowestPrice = getLowestPriceForCategory(category);

          return {
            category: category.category,
            title: category.translations.title,
            description: category.translations.description,
            image: category.media,
            status$: this.getStatusStream(category.category, numberOfUnpaidServices$, booking),
            price$: getPriceForCategoryStream(category.category, unpaidServices$, fallbackCurrencyCode$),
            count$: numberOfUnpaidServices$,
            purchasedCount$: getCountForCategoryStream(category.category, includedServices$),
            maxNumberItems$,
            maxPurchasesDone$: maxPurchasesDone$,
            notAvailable$,
            notEligibleWithIncompatibleSeat: notEligibleWithIncompatibleSeat,
            purchasesDoneForFragment$: purchasesDoneForFragment$,
            noQuotaLeft: noQuotaLeft,
            productCardNotification$: combineLatest([maxPurchasesDone$, numberOfUnpaidServices$, notAvailable$]).pipe(
              map(([maxPurchasesDone, count, notAvailable]) =>
                this.resolveProductCardNotification(
                  category.category,
                  booking,
                  maxPurchasesDone,
                  count,
                  noQuotaLeft,
                  notEligibleWithIncompatibleSeat,
                  notAvailable
                )
              ),
              finShare()
            ),
            lowestPrice: lowestPrice?.money,
            originalPrice: lowestPrice?.originalPrice,
            productTags: getProductTags(category.category, campaignsCategories, combinedServices?.[category.category], {
              showBestPriceNow: false,
              enableTierBenefitTag: false,
            }),
          };
        })
      )
    );

    this.productsReady$ = this.products$.pipe(map((products) => products.length > 0));

    this.subscriptions.add(
      this.route.queryParams.pipe(take(1)).subscribe((params) => {
        this.changeFlightModalOpen$.next(params['openChangeModal'] || false);
        if (params['openChangeModal']) {
          this.router.navigateByUrl(this.router.url.substring(0, this.router.url.indexOf('?')));
        }
        if (params['seatMap']) {
          this.tryToOpenSeatMap();
          this.router.navigate([], { queryParams: { seatMap: null }, queryParamsHandling: 'merge' });
        }
      })
    );

    this.subscriptions.add(
      this.booking$
        .pipe(
          take(1),
          filter((booking) => booking.otherInformation.isGroupBooking && !booking.passengers[0].email)
        )
        .subscribe(() => {
          this.showGroupBookingEmailModal$.next(true);
        })
    );

    this.groupEmailForm = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.pattern(EMAIL_PATTERN)]),
    });

    this.includedServices$ = this.booking$.pipe(
      map((booking) => getServicesToShow(booking.services, 'included')),
      finShare()
    );

    this.unconfirmedServices$ = this.booking$.pipe(
      map((booking) => getServicesToShow(booking.services, 'unconfirmed')),
      finShare()
    );

    this.upsellInModal$ = this.getUpsellInModalStream();

    this.content$ = this.fragmentService.getFragment<CmsArticle>('MMB.fragments.kidsInBusinessNotice.url').pipe(
      take(1),
      map((fragment) => fragment.items as CmsArticle[])
    );
    this.isBoardingPassAvailable$ = this.checkInService.isBoardingPassAvailable();
    this.unpaidItemsModalContent$ = this.isBoardingPassAvailable$.pipe(
      switchMap((isBoardingPassAvailable) =>
        this.fragmentService
          .getFragment<CmsArticle>(
            isBoardingPassAvailable
              ? 'MMB.fragments.checkIn.unpaidItemsBoardingPass.url'
              : 'MMB.fragments.checkIn.unpaidItemsCheckIn.url'
          )
          .pipe(take(1))
      ),
      finShare()
    );

    this.hasUnpaidServices$ = this.booking$.pipe(map((booking) => booking?.services?.unpaid?.length > 0));
  }

  ngOnDestroy(): void {
    this.feedbackService.setFeedbackWidgetVisibility(false);
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  onEmailModalClose(): void {
    this.showGroupBookingEmailModal$.next(false);
    this.groupEmailForm.controls.email.setValue('');
  }

  closeEmailModal(): void {
    this.showGroupBookingEmailModal$.next(false);
  }

  openEmailModal(value: boolean): void {
    this.showGroupBookingEmailModal$.next(value);
    this.isTravelExtraModal = value;
  }

  goToShoppingCart(): void {
    this.gtmService.trackElement(
      'continue-to-review-basket',
      GaContext.MANAGE_BOOKING,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK,
      `recLoc-${snapshot(this.booking$).otherInformation.analyticsToken}`
    );

    this.router.navigateByUrl(
      `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.REVIEW.path}`
    );
  }

  scrollTo(templateRef: ElementRef | HTMLElement, offsetY = 60): void {
    this.scrollService.smoothScroll(templateRef, { offsetY });
  }

  openKidsInBusinessModal(): void {
    this.kidsInBusinessModalOpen$.next(true);
  }

  triggerCheckInModalOpen(redirectionType: CheckInRedirectionType, openAsBlankPageForLegacy = false): void {
    this.subscriptions.add(
      this.hasUnpaidServices$.pipe(take(1)).subscribe((hasUnpaidServices) => {
        if (hasUnpaidServices) {
          this.openUnpaidItemsModal(redirectionType);
        } else {
          this.checkInService.triggerCheckInRedirection(redirectionType, false, false, openAsBlankPageForLegacy);
        }
      })
    );
  }

  openUnpaidItemsModal(redirectionType: CheckInRedirectionType): void {
    this.unpaidServicesInCartModal$.next({
      isOpen: true,
      checkInRedirection: redirectionType,
    });
  }

  closeKidsInBusinessModal(): void {
    this.kidsInBusinessModalOpen$.next(false);
  }

  closeUnpaidItemsModal(): void {
    this.unpaidServicesInCartModal$.next({ isOpen: false });
  }

  onSubmitEmail(): void {
    if (this.groupEmailForm.dirty && !this.groupEmailForm.disabled && this.groupEmailForm.valid) {
      this.groupEmailForm.disable();
      const { id, title, passengerTypeCode, firstName, lastName, phoneNumbers } = snapshot(this.pax$);

      this.loading$.next(true);

      this.subscriptions.add(
        this.mmbService
          .updateContactDetails({
            joinFinnairPlus: false,
            passengers: [
              {
                title,
                id,
                passengerTypeCode,
                firstName,
                lastName,
                email: this.groupEmailForm.controls.email.value,
                phoneNumbers,
              },
            ],
          })
          .subscribe({
            next: () => {
              this.trackSubmit('success');
              this.loading$.next(false);
              this.closeEmailModal();
            },
            error: () => {
              this.toasterService.addMessageToQueue({
                id: 'contact-update-failed',
                contentKey: 'MMB.errors.errorContactDetails',
                theme: NotificationTheme.ALERT,
              });
              this.trackSubmit('error', 'MMB.errors.errorContactDetails');
              this.loading$.next(false);
              this.closeEmailModal();
            },
          })
      );
    } else {
      this.groupEmailForm.markAllAsTouched();
      this.groupEmailForm.updateValueAndValidity();
    }
  }

  private getUpsellInModalStream(): Observable<UpsellCategoriesForBound> {
    return this.booking$.pipe(
      map((order) => {
        return order.bounds.reduce((all, bound) => {
          all[bound.id] = {
            [Category.CABIN_BAGGAGE]:
              passengersMissingCategoryService(Category.CABIN_BAGGAGE, order, bound) &&
              shouldWeSellForCategory(Category.CABIN_BAGGAGE, order, bound),
          };
          return all;
        }, {});
      }),
      distinctUntilChanged(),
      finShare()
    );
  }

  private trackSubmit(event: string, errorKey: string = undefined) {
    this.gtmService.trackElement(
      `update-details-group-email-modal_passenger-${event}`,
      GaContext.MANAGE_BOOKING,
      ElementTypes.FORM,
      errorKey ? `ERROR::${errorKey}` : undefined,
      ElementActions.SUBMIT
    );
  }

  onSelectProduct(data: SelectedServiceData): void {
    this.openAncillarySubview$.next(data);
  }

  onDisruptionWaiverChangeFlightsClicked(): void {
    this.changeFlightModalOpen$.next(true);
  }

  onContinuePurchase(): void {
    this.router.navigateByUrl(
      `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.REVIEW.path}`
    );
  }

  tryToOpenSeatMap(): void {
    this.subscriptions.add(
      this.productsReady$.pipe(take(1)).subscribe(() => {
        this.onSelectProduct({
          category: Category.SEAT,
          index: 0,
        });
      })
    );
  }

  redirectToCheckIn(): void {
    this.clearingServicesInProgress$.next(true);
    this.subscriptions.add(
      combineLatest([this.booking$, this.unpaidServicesInCartModal$])
        .pipe(
          take(1),
          switchMap(([order, modal]) => {
            return removeAllProducts(this.store$, this.mmbService, order).pipe(
              take(1),
              map(() => modal)
            );
          })
        )
        .subscribe((modal) => {
          this.goToCheckInAndCloseModal(modal.checkInRedirection);
        })
    );
  }

  private goToCheckInAndCloseModal(type: CheckInRedirectionType): void {
    this.checkInService.triggerCheckInRedirection(type);
    this.clearingServicesInProgress$.next(false);
    this.closeUnpaidItemsModal();
  }

  private resolveProductCardNotification(
    category: Category,
    booking: FinnairOrder,
    maxPurchasesDone: boolean,
    numberOfUnpaidServices: number,
    noQuotaLeft: boolean,
    notEligibleWithIncompatibleSeat: boolean,
    notAvailable: boolean
  ): ProductCardNotification {
    const notification = { theme: NotificationTheme.INFO };
    if (maxPurchasesDone) {
      return { ...notification, messageKey: `services.${category}.maxedPurchasesNotification` };
    }
    if (noQuotaLeft && numberOfUnpaidServices === 0) {
      return {
        ...notification,
        messageKey: `services.${category}.noQuotaLeft`,
        theme: NotificationTheme.WARNING,
      };
    }
    if (notEligibleWithIncompatibleSeat) {
      return {
        ...notification,
        messageKey: `services.${category}.hasIncompatibleSeat`,
        theme: NotificationTheme.WARNING,
      };
    }
    if (category === Category.SEAT && isSeatPurchaseClosedAfterCheckIn(booking)) {
      return { ...notification, messageKey: `services.${category}.notAvailableWhenCheckInClosed` };
    }
    if (notAvailable) {
      return { ...notification, messageKey: `services.${category}.notAvailableForSell` };
    }
    return notification;
  }
}
